import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { IS_NON_PROD } from 'src/constants/EnvironmentConstants';

interface RequireLdapAccessProps {
  /** Groups that grant access in ALL stages. */
  anyOf: string[];
  /** Groups that grant access ONLY in non-prod stages (dev/alpha). Use for developer
   *  overrides (e.g. DEV_LDAP) so they let devs test in alpha but never open the route in beta/prod. */
  nonProdOnly?: string[];
  /** Where to redirect when access is denied. Defaults to the landing page. */
  redirectTo?: string;
}

/**
 * Reusable, stage-aware route guard. Renders the nested route (<Outlet/>) when the
 * viewer belongs to any allowed LDAP group, otherwise redirects.
 *
 * `nonProdOnly` groups are folded into the allow-list only when IS_NON_PROD is true,
 * so a developer override applies in dev/alpha but not in beta/prod. Row-level scoping
 * still lives in the backend resolvers; this is a coarse route gate only.
 */
export const RequireLdapAccess = ({ anyOf, nonProdOnly = [], redirectTo = '/' }: RequireLdapAccessProps) => {
  const { userLDAPGroups } = useAuth();
  const allowedGroups = [...anyOf, ...(IS_NON_PROD ? nonProdOnly : [])];
  const hasAccess = allowedGroups.some((group) => userLDAPGroups?.includes(group));
  return hasAccess ? <Outlet /> : <Navigate to={redirectTo} replace />;
};
